import type {
  ContentChoice,
  Effect,
  EffectVocabulary,
  FormField,
  Resource,
} from '@rollcall/contracts'
import { useT } from './i18n-context.js'
import { ChoiceEditor } from './choice-editor.js'
import { ResourceEditor } from './resource-editor.js'
import { EffectEditor } from './effect-editor.js'
import type { TFunction } from 'i18next'

/**
 * Форма записи по описанию, пришедшему с сервера (D53, шаг 3).
 *
 * Поля не перечислены здесь ни для одного вида записи. Сервер считает
 * их из схемы — той самой, по которой валидируется SRD, — а этот файл
 * умеет рисовать восемь видов поля и ничего не знает про то, что
 * у предмета есть вес, а у заклинания уровень. Добавили поле в схему —
 * оно появилось в форме само.
 *
 * Поле, строение которого сервер не разобрал (`unsupported`), рисуется
 * не пустотой, а объяснением: такое заполняется копированием
 * существующей записи. Соврать про него хуже, чем признать.
 */

export type Body = Record<string, unknown>

export function valueAt(body: Body, path: string): unknown {
  let current: unknown = body
  for (const segment of path.split('.')) {
    if (typeof current !== 'object' || current === null) return undefined
    current = (current as Record<string, unknown>)[segment]
  }
  return current
}

/** Правка по пути: копия, а не мутация — состояние React живёт этим. */
export function withValue(body: Body, path: string, value: unknown): Body {
  const [head, ...rest] = path.split('.')
  if (head === undefined) return body
  if (rest.length === 0) return { ...body, [head]: value }
  const nested = body[head]
  const inner = typeof nested === 'object' && nested !== null ? (nested as Body) : {}
  return { ...body, [head]: withValue(inner, rest.join('.'), value) }
}

/** Перевод по ключу; ветка словаря вместо строки означает «нет». */
function labelOf(t: TFunction, key: string, fallback: string): string {
  // `returnObjects` здесь не украшение: без него ветка словаря
  // (`trait.armor` — это раздел с light, medium и heavy) возвращается
  // не объектом, а жалобой «returned an object instead of string»,
  // и жалоба уезжает прямо на экран.
  const translated: unknown = t(key, { returnObjects: true })
  if (typeof translated !== 'string') return fallback
  return translated === key ? fallback : translated
}

function fieldLabel(t: TFunction, field: FormField): string {
  return labelOf(t, `custom.field.${field.key}`, field.key)
}

function optionLabel(t: TFunction, value: string): string {
  return labelOf(t, `custom.option.${value}`, value)
}

/** Пустое значение поля: с него начинается новый элемент списка. */
export function emptyValue(field: FormField): unknown {
  switch (field.kind) {
    case 'number':
      return field.min ?? 0
    case 'boolean':
      return false
    case 'enum':
      return field.options?.[0] ?? ''
    case 'effects':
      return []
    case 'resources':
      return []
    case 'choices':
      return []
    case 'list':
      return []
    case 'group':
      return emptyGroup(field.fields ?? [])
    case 'variant': {
      const first = field.variants?.[0]
      if (first === undefined) return null
      return { ...emptyGroup(first.fields), category: first.value }
    }
    case 'unsupported':
      return field.fallback ?? null
    default:
      return ''
  }
}

function emptyGroup(fields: readonly FormField[]): Body {
  const out: Body = {}
  for (const field of fields) {
    out[field.key] = field.nullable && !field.required ? null : emptyValue(field)
  }
  return out
}

/** Различитель ветви: у всех размеченных объединений в схемах он один. */
const DISCRIMINATOR = 'category'

/**
 * Путь поля внутри переданного тела.
 *
 * У элемента списка тело своё, а пути в описании формы — полные
 * (`mechanics.levels.item.level`). Префикс отрезается здесь, а не
 * переписывается в описании: описание приходит с сервера и одно
 * на всю форму.
 */
function relativePath(path: string, prefix: string): string {
  return prefix === '' ? path : path.slice(prefix.length + 1)
}

function FieldView({
  field,
  body,
  prefix,
  vocabulary,
  onChange,
}: {
  readonly field: FormField
  readonly body: Body
  readonly prefix: string
  readonly vocabulary: EffectVocabulary | null
  readonly onChange: (path: string, value: unknown) => void
}) {
  const t = useT()
  const value = valueAt(body, relativePath(field.path, prefix))
  const label = fieldLabel(t, field)

  if (field.kind === 'effects') {
    const effects = Array.isArray(value) ? (value as Effect[]) : []
    if (vocabulary === null) return null
    return (
      <div className="app-field">
        <span className="app-field-label">{label}</span>
        <EffectEditor
          effects={effects}
          vocabulary={vocabulary}
          onChange={(next) => {
            onChange(field.path, [...next])
          }}
        />
      </div>
    )
  }

  if (field.kind === 'resources') {
    const resources = Array.isArray(value) ? (value as Resource[]) : []
    if (vocabulary === null) return null
    return (
      <div className="app-field">
        <span className="app-field-label">{label}</span>
        <ResourceEditor
          resources={resources}
          vocabulary={vocabulary}
          onChange={(next) => {
            onChange(field.path, [...next])
          }}
        />
      </div>
    )
  }

  if (field.kind === 'choices') {
    const choices = Array.isArray(value) ? (value as ContentChoice[]) : []
    return (
      <div className="app-field">
        <span className="app-field-label">{label}</span>
        <ChoiceEditor
          choices={choices}
          onChange={(next) => {
            onChange(field.path, [...next])
          }}
        />
      </div>
    )
  }

  if (field.kind === 'unsupported') {
    return (
      <p className="app-field-hint" data-testid={`unsupported-${field.path}`}>
        {t('custom.form.unsupported', { field: label })}
      </p>
    )
  }

  if (field.kind === 'group') {
    return (
      <fieldset className="app-fieldset">
        <legend>{label}</legend>
        <FieldList
          fields={field.fields ?? []}
          body={body}
          prefix={prefix}
          vocabulary={vocabulary}
          onChange={onChange}
        />
      </fieldset>
    )
  }

  if (field.kind === 'variant') {
    const chosen =
      typeof value === 'object' && value !== null
        ? String((value as Body)[DISCRIMINATOR] ?? '')
        : ''
    const variant = field.variants?.find((item) => item.value === chosen)
    return (
      <fieldset className="app-fieldset">
        <legend>{label}</legend>
        <label className="app-field">
          <span className="app-field-label">{t('custom.form.variant')}</span>
          <select
            value={chosen}
            onChange={(event) => {
              const next = field.variants?.find(
                (item) => item.value === event.target.value,
              )
              onChange(field.path, {
                ...emptyGroup(next?.fields ?? []),
                [DISCRIMINATOR]: event.target.value,
              })
            }}
          >
            {(field.options ?? []).map((option) => (
              <option key={option} value={option}>
                {optionLabel(t, option)}
              </option>
            ))}
          </select>
        </label>
        {variant !== undefined && (
          <FieldList
            fields={variant.fields}
            body={body}
            prefix={prefix}
            vocabulary={vocabulary}
            onChange={onChange}
          />
        )}
      </fieldset>
    )
  }

  if (field.kind === 'list') {
    const items = Array.isArray(value) ? value : []
    const inner = field.fields ?? []
    const scalar = inner.length === 1 && inner[0]?.key === 'item'
    return (
      <fieldset className="app-fieldset">
        <legend>{label}</legend>
        {items.map((item, index) => (
          <div className="app-list-row" key={`${field.path}-${index}`}>
            {scalar && inner[0] !== undefined ? (
              <ScalarItem
                field={inner[0]}
                value={item}
                onChange={(next) => {
                  onChange(
                    field.path,
                    items.map((old, at) => (at === index ? next : old)),
                  )
                }}
              />
            ) : (
              <FieldList
                fields={inner}
                body={(typeof item === 'object' && item !== null ? item : {}) as Body}
                prefix={`${field.path}.item`}
                vocabulary={vocabulary}
                // Элемент списка правится целиком: индекс знает только
                // этот уровень, и тащить его вниз через все вложенные
                // поля значило бы переписывать пути в описании формы.
                onChange={(path, next) => {
                  const updated = withValue(
                    (typeof item === 'object' && item !== null ? item : {}) as Body,
                    relativePath(path, `${field.path}.item`),
                    next,
                  )
                  onChange(
                    field.path,
                    items.map((old, at) => (at === index ? updated : old)),
                  )
                }}
              />
            )}
            <button
              type="button"
              className="app-button app-button-quiet"
              onClick={() => {
                onChange(
                  field.path,
                  items.filter((_, at) => at !== index),
                )
              }}
            >
              {t('custom.form.removeItem')}
            </button>
          </div>
        ))}
        <button
          type="button"
          className="app-button app-button-quiet"
          onClick={() => {
            const first = inner[0]
            onChange(field.path, [
              ...items,
              scalar && first !== undefined ? emptyValue(first) : emptyGroup(inner),
            ])
          }}
        >
          {t('custom.form.addItem')}
        </button>
      </fieldset>
    )
  }

  const nullable = field.nullable && !field.required
  const isNull = value === null || value === undefined

  return (
    <label className="app-field">
      <span className="app-field-label">{label}</span>
      {nullable && (
        // Строка с флажком, а не подсказка: подпись обязана
        // переключать флажок, а квадрат — стоять рядом с ней.
        <span className="app-check">
          <input
            type="checkbox"
            checked={!isNull}
            onChange={(event) => {
              onChange(field.path, event.target.checked ? emptyValue(field) : null)
            }}
          />
          {t('custom.form.present')}
        </span>
      )}
      {!isNull && (
        <ScalarItem
          field={field}
          value={value}
          onChange={(next) => {
            onChange(field.path, next)
          }}
        />
      )}
    </label>
  )
}

/** Одно значение: строка, число, флаг, перечисление, ссылка на запись. */
function ScalarItem({
  field,
  value,
  onChange,
}: {
  readonly field: FormField
  readonly value: unknown
  readonly onChange: (next: unknown) => void
}) {
  const t = useT()

  if (field.kind === 'boolean') {
    return (
      <span className="app-check">
        <input
          type="checkbox"
          checked={value === true}
          onChange={(event) => {
            onChange(event.target.checked)
          }}
        />
      </span>
    )
  }

  if (field.kind === 'number') {
    return (
      <input
        type="number"
        {...(field.min === undefined ? {} : { min: field.min })}
        {...(field.max === undefined ? {} : { max: field.max })}
        step={field.integer === true ? 1 : 'any'}
        value={typeof value === 'number' ? value : ''}
        onChange={(event) => {
          onChange(event.target.value === '' ? null : Number(event.target.value))
        }}
      />
    )
  }

  if (field.kind === 'enum') {
    return (
      <select
        // Имя списку нужно своё: элемент списка рисуется без подписи —
        // строка списка не `label`, и связать их нечем.
        aria-label={fieldLabel(t, field)}
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => {
          onChange(event.target.value)
        }}
      >
        {(field.options ?? []).map((option) => (
          <option key={option} value={option}>
            {optionLabel(t, option)}
          </option>
        ))}
      </select>
    )
  }

  if (field.kind === 'longText') {
    return (
      <textarea
        rows={4}
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => {
          onChange(event.target.value)
        }}
      />
    )
  }

  // Ссылка на запись остаётся строкой: справочник для неё приходит
  // отдельным запросом, и подставляется он на шаге выбора образца.
  return (
    <input
      value={typeof value === 'string' ? value : ''}
      placeholder={field.kind === 'contentRef' ? t('custom.form.refHint') : ''}
      onChange={(event) => {
        onChange(event.target.value)
      }}
    />
  )
}

export function FieldList({
  fields,
  body,
  vocabulary,
  onChange,
  prefix = '',
  skipEffects = false,
}: {
  readonly fields: readonly FormField[]
  readonly body: Body
  readonly vocabulary: EffectVocabulary | null
  readonly onChange: (path: string, value: unknown) => void
  /** Префикс пути, если тело — элемент списка. */
  readonly prefix?: string
  /** Эффекты живут на своём шаге: в общей форме их не показываем. */
  readonly skipEffects?: boolean
}) {
  return (
    <>
      {fields
        .filter((field) => !(skipEffects && field.kind === 'effects'))
        .map((field) => (
          <FieldView
            key={field.path}
            field={field}
            body={body}
            prefix={prefix}
            vocabulary={vocabulary}
            onChange={onChange}
          />
        ))}
    </>
  )
}

/** Начальное тело записи: пустое по описанию формы. */
export function emptyBody(fields: readonly FormField[]): Body {
  return emptyGroup(fields)
}
